import { ComputedRef, nextTick, onBeforeUnmount, onMounted, Ref, ref, ShallowRef, watch } from 'vue';

import { toComponentEl, toElement } from '../../utils/templateRefNarrowing';

export interface UseActionsWidthOptions {
  toolbarEl: ShallowRef<unknown>;
  paginationEl: ShallowRef<unknown>;
  checkboxEl: ShallowRef<unknown>;
  isPaginationEnabled: ComputedRef<boolean> | undefined;
  isSelectable: Ref<boolean> | undefined;
  isEmpty: ComputedRef<boolean> | undefined;
}

export function useActionsWidth(options: UseActionsWidthOptions) {
  const { toolbarEl, paginationEl, checkboxEl, isPaginationEnabled, isSelectable, isEmpty } = options;

  const actionsWidth = ref<string | null>(null);
  let toolbarResizeObserver: ResizeObserver | null = null;
  let paginationResizeObserver: ResizeObserver | null = null;

  const updateActionsWidth = () => {
    const toolbarElVal = toElement(toolbarEl.value);
    if (!toolbarElVal) return;

    const toolbarWidth = toolbarElVal.getBoundingClientRect().width;
    let paginationWidth = 0;
    let checkboxWidth = 0;

    // Calculate pagination width if pagination is enabled
    if (isPaginationEnabled?.value) {
      const paginationElVal = toElement(paginationEl.value);
      if (paginationElVal) paginationWidth = paginationElVal.getBoundingClientRect().width;
    }

    // Calculate checkbox width if selectable and not empty
    if (isSelectable?.value && !isEmpty?.value) {
      const checkboxElVal = toComponentEl(checkboxEl.value);
      if (checkboxElVal) checkboxWidth = checkboxElVal.getBoundingClientRect().width;
    }

    // Get computed styles to calculate padding
    const computedStyle = window.getComputedStyle(toolbarElVal);
    const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
    const paddingRight = parseFloat(computedStyle.paddingRight) || 0;

    const availableWidth = Math.max(0, toolbarWidth - paginationWidth - checkboxWidth - paddingLeft - paddingRight);
    actionsWidth.value = `${availableWidth}px`;
  };

  const setupResizeObserver = () => {
    const toolbarElVal = toElement(toolbarEl.value);
    if (!toolbarElVal) return;

    // Observe toolbar element
    toolbarResizeObserver = new ResizeObserver(() => {
      nextTick(() => {
        updateActionsWidth();
      });
    });
    toolbarResizeObserver.observe(toolbarElVal);

    // Observe pagination element if it exists
    const paginationElVal = toElement(paginationEl.value);
    if (paginationElVal) {
      paginationResizeObserver = new ResizeObserver(() => {
        nextTick(() => {
          updateActionsWidth();
        });
      });
      paginationResizeObserver.observe(paginationElVal);
    }
  };

  const cleanupResizeObserver = () => {
    if (toolbarResizeObserver) {
      toolbarResizeObserver.disconnect();
      toolbarResizeObserver = null;
    }
    if (paginationResizeObserver) {
      paginationResizeObserver.disconnect();
      paginationResizeObserver = null;
    }
  };

  onMounted(() => {
    nextTick(() => {
      updateActionsWidth();
      setupResizeObserver();
    });
  });

  onBeforeUnmount(() => {
    cleanupResizeObserver();
  });

  // Watch for changes in conditions that affect element visibility
  watch([isPaginationEnabled, isSelectable, isEmpty], () => {
    nextTick(() => {
      updateActionsWidth();
    });
  });

  // Watch for pagination element changes to setup/cleanup observer
  watch(paginationEl, (newPaginationEl) => {
    // Cleanup old observer if it exists
    if (paginationResizeObserver) {
      paginationResizeObserver.disconnect();
      paginationResizeObserver = null;
    }

    // Setup new observer if pagination element exists
    const el = toElement(newPaginationEl);
    if (el) {
      paginationResizeObserver = new ResizeObserver(() => {
        nextTick(() => {
          updateActionsWidth();
        });
      });
      paginationResizeObserver.observe(el);
    }
  });

  return {
    actionsWidth,
    updateActionsWidth,
  };
}
